<template>
  <FloatingPillButton
    :disabled="disabled"
    @click="currentWidgetConfig?.action"
    class="z-20"
    :variant="currentWidgetConfig.variant"
  >
    <template v-if="!isEmptyState" #start>
      <component :is="iconComponent" />
    </template>

    <template v-if="!persistentStore.generatingBackground && !isEmptyState" #center>
      {{ currentWidgetConfig.copy }}
    </template>

    <template v-if="!persistentStore.generatingBackground && !isEmptyState && currentWidgetConfig.badgeAvailable" #end>
      <Badge v-if="shouldShowLoading" tone="secondary" is-small>
        <div class="loading-animation-container">
          <Spinner />
        </div>
      </Badge>
      <Badge v-else :tone="badgeTone" is-small>
        <div class="flex items-center">
          <span :class="currentState === WidgetState.Error ? 'text-white' : 'text-typo'" class="px-1 font-bold">{{
            badgeText
          }}</span>
        </div>
      </Badge>
    </template>
  </FloatingPillButton>
</template>

<script setup lang="ts">
import { FloatingPillButton, Badge, SparkleIcon, CircularLoader, AiBackgroundIconAll, Spinner } from "prism";
import { nextTick, ref, computed, watch, inject, onMounted } from "vue";
import { useMagicBackground, WidgetState } from "@/composables/magic_background";
import { MagicBackgroundTemplate } from "@/modules/internal_api/magic_backgrounds";
import { PersistentStore } from "@/stores/persistent_store";
import { useEditorStore } from "@/stores/editor_store";
import User from "@/modules/user";

interface Props {
  template: MagicBackgroundTemplate;
  persistentStore: PersistentStore;
}

const props = defineProps<Props>();
const I18n = inject("I18n");
const editorStore = useEditorStore();

const isEmptyState = ref(false);

const persistentStore = computed(() => props.persistentStore);
const { currentWidgetConfig, currentState, resetError, fetchCredits, isLoadingCredits } =
  useMagicBackground(persistentStore);

const disabled = computed(() => {
  return (
    persistentStore.value.generatingBackground ||
    isLoadingCredits.value ||
    editorStore.isProcessingMagicBackgroundHdDownload
  );
});

const shouldShowLoading = computed(() => {
  return isLoadingCredits.value && currentState.value !== WidgetState.Error;
});

const iconComponent = computed(() => {
  switch (currentWidgetConfig.value?.icon) {
    case "generate":
      return AiBackgroundIconAll;
    case "generating":
      return CircularLoader;
    case "generated":
      return AiBackgroundIconAll;
    default:
      return SparkleIcon;
  }
});

const badgeTone = computed(() => (currentState.value === WidgetState.Error ? "red" : "primary"));
const badgeText = computed(() => {
  switch (currentState.value) {
    case WidgetState.Error:
      return I18n.t("editor.refunded");
    case WidgetState.LoggedOut:
      return I18n.t("editor.download_menu.unlock");
    default:
      return I18n.t("dashboard.earn_reward");
  }
});

onMounted(() => {
  fetchCredits();
});

watch(currentState, (newVal) => {
  if (newVal === WidgetState.Error) {
    setTimeout(() => {
      resetError();
      isEmptyState.value = true;
      nextTick(() => {
        isEmptyState.value = false;
      });
    }, 2000);
  }
});
</script>

<style scoped>
.loading-animation-container {
  scale: 0.6;
}
</style>
